import * as d3 from 'd3';
import i18next from 'i18next';

import { gen_data_group } from "../data_processing/ms_line_chart_group.js";

/**
 * Wraps the text with a callout path of the correct size, as measured in the page.
 *
 * @param {Object} text - The D3 selection of the text element.
 * @param {Object} path - The D3 selection of the path element.
 */
function size(text, path) {
    const { x, y, width: w, height: h } = text.node().getBBox();
    const r = 10;
    text.attr("transform", `translate(${-w / 2},${15 - y})`);
    path.attr("d", `M${-w / 2 - 10 + r},5
                H-5
                l5,-5
                l5,5
                H${w / 2 + 10 - r}
                a${r},${r} 0 0 1 ${r},${r}
                v${h + 20 - 2 * r}
                a${r},${r} 0 0 1 -${r},${r}
                H${-w / 2 - 10 + r}
                a${r},${r} 0 0 1 -${r},-${r}
                v-${h + 20 - 2 * r}
                a${r},${r} 0 0 1 ${r},-${r}
                z`);
}

/**
 * Formats a date object into a readable string.
 *
 * @param {Date} date - The date object to format.
 * @returns {string} - The formatted date string.
 */
function formatDate(date) {
    return date.toLocaleString("en", {
        month: "short",
        day: "numeric",
        year: "numeric"
    });
}

/**
 * Generates a line chart over time using D3.js.
 *
 * @param {string} container_id - The ID of the HTML element where the chart will be appended.
 * @param {Array} data - The data array used to generate the line chart.
 * @returns {Object} - The generated SVG node.
 */
export function line_chart_over_time(container_id, data) {
    const margin = { top: 20, right: 30, bottom: 60, left: 100 };
    const width = 1400;
    const height = 400;

    // Declare the x (horizontal position) scale.
    const x = d3.scaleUtc()
                .domain(d3.extent(data, d => d.date))
                .range([margin.left, width - margin.right]);

    // Declare the y (vertical position) scale.
    const y = d3.scaleLinear()
                .domain([d3.min(data, d => +d.value), d3.max(data, d => +d.value)])
                .range([height - margin.bottom, margin.top]);

    // Declare the line generator.
    const line = d3.line()
                   .x(d => x(d.date))
                   .y(d => y(+d.value));

    // Declare the area generator.
    const area = d3.area()
        .x(d => x(d.date))
        .y0(y(d3.min(data, d => +d.value)))
        .y1(d => y(+d.value));

    // Create the SVG container.
    const svg = d3.select(`#${container_id}`)
                  .append("svg")
                  .attr("viewBox", [0, 0, width, height])
                  .attr("width", width)
                  .attr("height", height)
                  .attr("style", "max-width: 100%; height: auto; height: intrinsic; font: 10px sans-serif;")
                  .style("-webkit-tap-highlight-color", "transparent")
                  .style("overflow", "visible")
                  .on("pointerenter pointermove", pointermoved)
                  .on("pointerleave", pointerleft)
                  .on("touchstart", event => event.preventDefault());

    // Add the x-axis.
    svg.append("g")
       .attr("transform", `translate(0,${height - margin.bottom})`)
       .transition()
       .duration(750)
       .call(d3.axisBottom(x).ticks(width / 70).tickSizeOuter(0).tickFormat(d3.timeFormat("%Y-%m-%d")))
       .selectAll("text")  
            .style("text-anchor", "end")
            .attr("dx", "-.8em")
            .attr("dy", ".15em")
            .attr("transform", "rotate(-45)" );

    // Add the y-axis, remove the domain line, add grid lines and a label.
    svg.append("g")
       .attr("transform", `translate(${margin.left},0)`)
       .call(d3.axisLeft(y).ticks(height / 60))
       .call(g => g.select(".domain").remove())
       .call(g => g.selectAll(".tick line").clone()
                   .attr("x2", width - margin.left - margin.right)
                   .attr("stroke-opacity", 0.1))
       .call(g => g.append("text")
                   .attr("x", -margin.left)
                   .attr("y", 10)
                   .attr("fill", "currentColor")
                   .attr("text-anchor", "start")
                   .attr("data-i18n", "label-daily-dataset-num")
                   .text(i18next.t('label-daily-dataset-num'))
        );

    // Append a path for the line.
    svg.append("path")
       .attr("fill", "none")
       .style("stroke", "#00A3A4")
       .style("stroke-width", 3)
       .attr("d", line(data));

    // Append a path for the area (under the axes).
    svg.append("path")
        .attr("fill", "url(#gradient3)")
        .attr("d", area(data));

    // Create the vertical line.
    const verticalLine = svg.append("line")
                            .attr("stroke", "#fdab13")
                            .attr("stroke-width", 1)
                            .attr("y1", margin.top)
                            .attr("y2", height - margin.bottom)
                            .style("display", "none");

    // Create the data point circle.
    const dataPoint = svg.append("circle")
                         .attr("r", 5)
                         .attr("fill", "#00C2A5")
                         .style("display", "none");

    // Create the tooltip container.
    const tooltip = svg.append("g");

    // Add the event listeners that show or hide the tooltip.
    const bisect = d3.bisector(d => d.date).center;

    function pointermoved(event) {     
        const i = bisect(data, x.invert(d3.pointer(event)[0]));
        tooltip.style("display", null);
        tooltip.attr("transform", `translate(${x(data[i].date)},${y(data[i].value)})`);

        verticalLine.attr("x1", x(data[i].date))
                    .attr("x2", x(data[i].date))
                    .style("display", null);

        dataPoint.attr("cx", x(data[i].date))
                 .attr("cy", y(data[i].value))
                 .style("display", null);

        const path = tooltip.selectAll("path")
                            .data([,])
                            .join("path")
                            .attr("fill", "rgba(0, 0, 0, 0.6)")
                            .attr("stroke", "black")
                            .style("stroke-width", 0);

        const text = tooltip.selectAll("text")
                            .data([,])
                            .join("text")
                            .call(text => text
                                .selectAll("tspan")
                                .data([formatDate(data[i].date), data[i].value])
                                .style('fill', '#fff')
                                .join("tspan")
                                .attr("x", 0)
                                .attr("y", (_, i) => `${i * 1.1}em`)
                                .attr("font-size", "16px")
                                .attr("font-weight", (_, i) => i ? null : "bold")
                                .text(d => d));

        size(text, path);
    }

    function pointerleft() {
        tooltip.style("display", "none");
        verticalLine.style("display", "none");
        dataPoint.style("display", "none");
    }

    return svg.node();
}

/**
 * Updates or creates a line chart using D3.js.
 *
 * @param {string} container_id - The ID of the HTML element where the chart will be appended.
 * @param {Array} data - The data array used to generate the line chart.
 * @returns {SVGElement} - The generated or updated SVG element containing the line chart.
 */
export function update_line_chart(container_id, data) {
    const svg = d3.select(`#${container_id} svg`);
    // Create
    if (svg.empty()) {
        return line_chart_over_time(container_id, data);
    } 
    // Update
    else {
        svg.remove();
        return line_chart_over_time(container_id, data);
    }
}

/**
 * Generates a multi-series line chart over time using D3.js.
 *
 * @param {string} container_id - The ID of the HTML element where the chart will be appended.
 * @param {string} legend_id - The ID of the HTML element where the legend will be appended.
 * @param {Array} dataset - The data array used to generate the line chart.
 * @param {Array} selected_data - The selected data array to be highlighted in the chart.
 * @returns {Map} - The generated data groups.
 */
export function ms_line_chart_over_time(container_id, legend_id, dataset, selected_data) {
    const margin = { top: 30, right: 30, bottom: 20, left: 100 };
    const width = 1000;
    const height = 500;

    // Declare the x (horizontal position) scale.
    const x = d3.scaleUtc().range([margin.left, width - margin.right]).domain(d3.extent(dataset, d => d.date));
                
    // Declare the y (vertical position) scale.
    const y = d3.scaleLinear().range([height - margin.bottom, margin.top])//.tickFormat(d3.format("d"));

    // Modify y domain to fix selected data
    if (selected_data.length > 0) {
        let counts = selected_data.flatMap(selected => {
            return dataset
                .filter(data => data.display_name === selected.text)
                .map(data => data.count);
        });
        let maxCount = d3.max(counts);
        let minCount = d3.min(counts);
        y.domain([minCount, maxCount]);
    } else {
        y.domain([d3.min(dataset, d => d.count), d3.max(dataset, d => d.count)]);
    }

    // process selected data
    const [points, groups] = gen_data_group(x, y, dataset);

    let display_groups = [];
    let display_groups_map = {};

    if (selected_data.length == 0) {
        // init top10
        display_groups = Array.from(groups.entries()).slice(0, 10);
    } else if (selected_data.length > 0) {
        // Add selected data to display_gorups_map
        selected_data.forEach((item) => {
            const new_group = [];
            const group_key = item.text;
            const group_value = groups.get(group_key);

            new_group[0] = group_key;
            new_group[1] = group_value;
            display_groups.push(new_group);
        })
    }
    display_groups_map = new Map(display_groups);
    let display_points = display_groups.flatMap(item => item[1]);

    // Create the SVG container.
    const svg = d3.select(`#${container_id}`)
                  .append("svg")
                  .attr("viewBox", [0, 0, width, height])
                  .attr("width", width)
                  .attr("height", height)
                  .attr("style", "max-width: 100%; height: auto; height: intrinsic; font: 10px sans-serif;")
                  .style("-webkit-tap-highlight-color", "transparent")
                  .style("overflow", "visible");

    // Add the x-axis.
    svg.append("g")
       .attr("transform", `translate(0,${height - margin.bottom})`)
       .transition()
       .duration(750)
       .call(d3.axisBottom(x).ticks(width / 70).tickSizeOuter(0).tickFormat(d3.timeFormat("%Y-%m-%d")))
       .selectAll("text")  
            .style("text-anchor", "end")
            .attr("dx", "-.8em")
            .attr("dy", ".15em")
            .attr("transform", "rotate(-45)" );

    // Add the y-axis, remove the domain line, add grid lines and a label.
    svg.append("g")
       .attr("transform", `translate(${margin.left},0)`)
       .call(d3.axisLeft(y).ticks(height / 60).tickFormat(d3.format("d")))
       .call(g => g.select(".domain").remove())
       .call(g => g.selectAll(".tick line").clone()
                   .attr("x2", width - margin.left - margin.right)
                   .attr("stroke-opacity", 0.1))
       .call(g => g.append("text")
                   .attr("x", -margin.left)
                   .attr("y", 10)
                   .attr("fill", "currentColor")
                   .attr("text-anchor", "start")
                   .attr("data-i18n", "label-daily-ms-dataset-num")
                   .text(i18next.t('label-daily-ms-dataset-num'))
        );

    // Define the color scale with a color scheme.
    const color = d3.scaleOrdinal(d3.schemeObservable10);

    // Draw the lines.
    const line = d3.line();
    const path = svg.append("g")
        .attr("fill", "none")
        .attr("stroke-width", 2)
        .attr("stroke-linejoin", "round")
        .attr("stroke-linecap", "round")
        .selectAll("path")
        .data(display_groups)
        .join("path")
        .style("mix-blend-mode", "multiply")
        // .attr("stroke", ([key]) => color(key))
        // .attr("d", line);
        .attr("stroke", ([key, values]) => color(key))
        .attr("d", ([key, values]) => line(values));

    // Add an invisible layer for the interactive tip.
    const dot = svg.append("g")
        .attr("display", "none");

    dot.append("circle")
        .attr("r", 2.5);

    dot.append("text")
        .attr("text-anchor", "middle")
        .attr("y", -8);

    svg
        .on("pointerenter", pointerentered)
        .on("pointermove", pointermoved)
        .on("pointerleave", pointerleft)
        .on("touchstart", event => event.preventDefault());

    // Create the tooltip container
    const tooltip = svg.append("g");

    // Legend
    const legend_data = [...display_groups_map.keys()];
    let legend = d3.select(`#${legend_id}`);

    // <ul>
    let legend_list = legend.append("ul")
        .attr("class", "m-1")
        .style("list-style-type", "none")

    // Each <li>
    legend_list.selectAll("li")
        .data(legend_data)
        .enter()
        .append("li")
        .append("div")
        .attr("class", "row align-items-center mb-3")
        .each(function(d) {
            // Add one rect in the legend for each name.
            d3.select(this).append("div")
                .attr("class", "col-1")
                .style("width", "10px")
                .style("height", "15px")
                .style("background-color", color(d));

            // Add one dot in the legend for each name.
            d3.select(this).append("div")
                .attr("class", "col-10")
                .style("line-height", ".7em")
                .append("span")
                .style("color", "Dimgray")
                .style("font-size", "11px")
                .text(d);
        });

    function pointermoved(event) {
        const [xm, ym] = d3.pointer(event);
        const i = d3.leastIndex(display_points, ([x, y]) => Math.hypot(x - xm, y - ym));
        const [x, y, k] = display_points[i];
        path.style("stroke", ([key]) => key === k ? null : "#ddd").filter(([key]) => key === k).raise();
        dot.attr("transform", `translate(${x},${y})`);
        dot.select("text").text(k).attr("font-size", "15px");
        svg.property("value", dataset[i]).dispatch("input", {bubbles: true});

        tooltip.style("display", null);
        tooltip.attr("transform", `translate(${x},${y})`);

        const tooltip_path = tooltip.selectAll("path")
                            .data([,])
                            .join("path")
                            .attr("fill", "rgba(0, 0, 0, 0.6)")
                            .attr("stroke", "black")
                            .style("stroke-width", 0);

        const text = tooltip.selectAll("text")
                            .data([,])
                            .join("text")
                            .call(text => text
                                .selectAll("tspan")
                                .data([formatDate(display_points[i][3]), display_points[i][4]])
                                .style('fill', '#fff')
                                .join("tspan")
                                .attr("x", 0)
                                .attr("y", (_, i) => `${i * 1.1}em`)
                                .attr("font-size", "16px")
                                .attr("font-weight", (_, i) => i ? null : "bold")
                                .text(d => d));

        size(text, tooltip_path);
    }

    function pointerentered() {
        path.style("mix-blend-mode", null).style("stroke", "#ddd");
        dot.attr("display", null);
    }

    function pointerleft() {
        tooltip.style("display", "none");
        path.style("mix-blend-mode", "multiply").style("stroke", null);
        dot.attr("display", "none");
        svg.node().value = null;
        svg.dispatch("input", {bubbles: true});
    }

    return groups
}

/**
 * Updates or creates a multi-series line chart using D3.js.
 *
 * @param {string} container_id - The ID of the HTML element where the chart will be appended.
 * @param {string} legend_id - The ID of the HTML element where the legend will be appended.
 * @param {Array} dataset - The data array used to generate the line chart.
 * @param {Array} selected_data - The selected data array to be highlighted in the chart.
 * @returns {Map} - The generated data groups.
 */
export function update_ms_line_chart(container_id, legend_id, dataset, selected_data) {
    const svg = d3.select(`#${container_id} svg`);
    const legend_svg = d3.select(`#${legend_id} ul`);
    // Create
    if (svg.empty()) {
        return ms_line_chart_over_time(container_id, legend_id, dataset, selected_data);
    } 
    // Update
    else {
        svg.remove();
        legend_svg.remove();
        return ms_line_chart_over_time(container_id, legend_id, dataset, selected_data);
    }
}